/**
 * Extract action items (tasks) from meeting notes / transcript.
 *
 * Detects common patterns:
 *  - "TODO: something"
 *  - "Action: something"
 *  - "- [ ] something" (markdown checkbox)
 *  - "- something" (bullet points, when line looks like an action)
 *  - "* something" (asterisk bullets)
 *  - Lines starting with action verbs: "Create", "Send", "Review", etc.
 *  - "@name" mentions extracted as suggested assignees
 */

const ACTION_PREFIXES = [
    /^\s*todo[:\-]\s*/i,
    /^\s*action(?:\s+item)?[:\-]\s*/i,
    /^\s*task[:\-]\s*/i,
    /^\s*-\s*\[\s*\]\s*/,        // markdown checkbox
    /^\s*\[\s*\]\s*/              // bare checkbox
];

const BULLET_PREFIXES = [
    /^\s*[-*•]\s+/,
    /^\s*\d+[.)]\s+/              // 1. or 1)
];

const ACTION_VERBS = [
    'create', 'make', 'build', 'write', 'send', 'review', 'check', 'verify',
    'update', 'fix', 'schedule', 'organize', 'plan', 'prepare', 'design',
    'implement', 'test', 'deploy', 'investigate', 'research', 'follow up',
    'contact', 'email', 'call', 'setup', 'configure', 'document', 'discuss'
];

const MENTION_REGEX = /@([a-zA-Z][a-zA-Z0-9_.-]{1,30})/g;

const startsWithActionVerb = (text) => {
    const first = text.trim().split(/\s+/)[0]?.toLowerCase();
    if (!first) return false;
    return ACTION_VERBS.includes(first);
};

const stripPrefix = (line) => {
    for (const re of ACTION_PREFIXES) {
        if (re.test(line)) return line.replace(re, '').trim();
    }
    for (const re of BULLET_PREFIXES) {
        if (re.test(line)) return line.replace(re, '').trim();
    }
    return line.trim();
};

const hasActionPrefix = (line) => ACTION_PREFIXES.some((re) => re.test(line));
const hasBulletPrefix = (line) => BULLET_PREFIXES.some((re) => re.test(line));

const extractMentions = (text) => {
    const mentions = new Set();
    let m;
    while ((m = MENTION_REGEX.exec(text)) !== null) {
        mentions.add(m[1]);
    }
    return Array.from(mentions);
};

const cleanTitle = (text) => {
    // Remove @mentions from title (they'll be separate)
    return text.replace(MENTION_REGEX, '').replace(/\s+/g, ' ').trim();
};

/**
 * Parse transcript and return extracted action items.
 * @param {string} transcript
 * @returns {Array<{ title: string, mentions: string[], confidence: 'high'|'medium'|'low' }>}
 */
export const extractActionItems = (transcript = '') => {
    if (!transcript || typeof transcript !== 'string') return [];

    const lines = transcript.split(/\r?\n/);
    const items = [];
    const seen = new Set();

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line || line.length < 5) continue;

        let confidence = null;

        if (hasActionPrefix(line)) {
            confidence = 'high';
        } else if (hasBulletPrefix(line)) {
            // Bullet point — only keep if it looks like an action (verb-led or short enough)
            const stripped = stripPrefix(line);
            if (startsWithActionVerb(stripped) || (stripped.length > 10 && stripped.length < 200)) {
                confidence = 'medium';
            }
        } else if (startsWithActionVerb(line) && line.length < 200) {
            confidence = 'low';
        }

        if (!confidence) continue;

        const stripped = stripPrefix(line);
        const mentions = extractMentions(stripped);
        const title = cleanTitle(stripped);

        if (!title || title.length < 5) continue;

        // Dedupe (case-insensitive)
        const key = title.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);

        items.push({ title, mentions, confidence });
    }

    return items;
};
